import { createContext, useContext, useMemo } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { getTestimonials, preloadPortfolioData } from '../firebase';
import { useLoading } from './LoadingContext';
import { logError } from '../utils/error-handler';

const TestimonialsContext = createContext(null);

export const TestimonialsProvider = ({ children }) => {
  const [testimonials, setTestimonials] = useState(getTestimonials() || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { finishTask, beginExpectedTasks } = useLoading();

  const loadTestimonials = useCallback(async () => {
    setLoading(true);
    setError(null);
    beginExpectedTasks(['testimonials']);

    try {
      await preloadPortfolioData();
      const testimonialsData = getTestimonials();
      setTestimonials(testimonialsData || []);
    } catch (err) {
      logError(err, 'TestimonialsProvider', {});
      setError(err);
    } finally {
      setLoading(false);
      finishTask('testimonials');
    }
  }, [beginExpectedTasks, finishTask]);

  const switchLanguageClientSide = useCallback(() => {
    beginExpectedTasks(['testimonials']);

    requestAnimationFrame(() => {
      try {
        const testimonialsData = getTestimonials();
        setTestimonials(testimonialsData || []);
      } catch (err) {
        logError(err, 'switchLanguageClientSide - Testimonials', {});
        loadTestimonials();
      } finally {
        finishTask('testimonials');
      }
    });
  }, [beginExpectedTasks, finishTask, loadTestimonials]);

  useEffect(() => {
    let isMounted = true;
    loadTestimonials();

    const handleLanguageChange = () => {
      if (isMounted) {
        switchLanguageClientSide();
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      isMounted = false;
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [loadTestimonials, switchLanguageClientSide]);

  const value = useMemo(
    () => ({ testimonials, loading, error }),
    [testimonials, loading, error]
  );

  return (
    <TestimonialsContext.Provider value={value}>
      {children}
    </TestimonialsContext.Provider>
  );
};

export const useTestimonials = () => {
  const context = useContext(TestimonialsContext);
  if (!context) {
    throw new Error('useTestimonials must be used within TestimonialsProvider');
  }
  return context;
};
